import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';

import {
  InputDatePicker,
  InputEmail,
  InputLogo,
  InputPanel,
  InputPhone,
  InputTextBox,
} from '@ta/form-model';
import { isNonNullable } from '@ta/utils';

import { Status, UserProfile } from '../dto/user';
import { UpdateUserInput } from './dto/updateUserInput';
import { formatFieldVisibilities, getFieldVisibility, mkField } from './form-utils';

export enum EUserFormFields {
  firstname = 'firstname',
  lastname = 'lastname',
  email = 'email',
  picture = 'picture',
  dateOfBirth = 'dateOfBirth',
  phone = 'phone',
}

@Injectable({
  providedIn: 'root',
})
export class UserProfileFormService {
  public getBasicForm(user: UserProfile | null) {
    const getVisibility = (fieldName: string, defaultValue = true) =>
      getFieldVisibility(user?.fieldVisibilities, fieldName, defaultValue);

    const isLocked = user?.status === Status.Validated;

    const panelClass = 'card highlight-title';

    return [
      new InputPanel({
        key: 'panel-1',
        contentClass: 'grid g-space-md',
        children: [
          new InputPanel({
            key: 'panel-2',
            class: 'full',
            containerClass: [panelClass],
            label: 'app.forms.user.panel.profile_photo',
            children: [
              new InputLogo({
                key: EUserFormFields.picture,
                value: {
                  description: '',
                  id: '',
                  url: user?.picture ?? '',
                  size: 0,
                },
              }),
            ],
          }),
          new InputPanel({
            key: 'panel-3',
            class: 'full',
            containerClass: [panelClass],
            label: 'app.forms.user.panel.identity',
            contentClass: 'grid g-space-md',
            children: [
              new InputTextBox({
                key: EUserFormFields.firstname,
                label: 'app.forms.user.field.firstname',
                validators: [Validators.required],
                value: user?.firstname,
                class: 'mobile-full one-half',
                disabled: isLocked,
              }),
              new InputTextBox({
                key: EUserFormFields.lastname,
                label: 'app.forms.user.field.lastname',
                validators: [Validators.required],
                value: user?.lastname,
                class: 'mobile-full one-half',
                disabled: isLocked,
              }),
            ],
          }),
          mkField({
            key: EUserFormFields.email,
            label: 'app.forms.user.field.email',
            icon: 'mail',
            class: 'mobile-full one-third',
            visibleKey: 'email_visible',
            visibleValue: getVisibility(EUserFormFields.email),
            field: new InputEmail({
              key: EUserFormFields.email,
              disabled: true,
              validators: [Validators.required],
              value: user?.email,
              class: 'full',
            }),
          }),
          mkField({
            key: EUserFormFields.dateOfBirth,
            label: 'app.forms.user.field.date_of_birth',
            icon: 'cake',
            class: 'mobile-full one-third',
            visibleKey: 'dateOfBirth_visible',
            visibleValue: getVisibility(EUserFormFields.dateOfBirth),
            field: new InputDatePicker({
              key: EUserFormFields.dateOfBirth,
              validators: [Validators.required],
              value: user?.dateOfBirth,
              class: 'full',
              disabled: isLocked,
            }),
          }),
          mkField({
            key: EUserFormFields.phone,
            label: 'app.forms.user.field.phone',
            icon: 'phone',
            class: 'mobile-full one-third',
            visibleKey: 'phone_visible',
            visibleValue: getVisibility(EUserFormFields.phone),
            field: new InputPhone({
              key: EUserFormFields.phone,
              value: user?.phoneNumber,
              class: 'full',
              disabled: isLocked,
            }),
          }),
        ],
      }),
    ];
  }

  public getNameForm(user: UserProfile | null) {
    return [
      new InputPanel({
        key: 'panel-4',
        containerClass: ['card'],
        contentClass: 'grid g-space-md',
        children: [
          new InputTextBox({
            key: EUserFormFields.firstname,
            label: 'app.forms.user.field.firstname',
            validators: [Validators.required],
            value: user?.firstname,
            class: 'mobile-full one-half',
          }),
          new InputTextBox({
            key: EUserFormFields.lastname,
            label: 'app.forms.user.field.lastname',
            validators: [Validators.required],
            value: user?.lastname,
            class: 'mobile-full one-half',
          }),
        ],
      }),
    ];
  }

  public formatNameForm(data: unknown): Pick<UpdateUserInput, 'firstname' | 'lastname'> {
    const form = data as Record<string, unknown>;
    return {
      firstname: form[EUserFormFields.firstname] as string,
      lastname: form[EUserFormFields.lastname] as string,
    };
  }

  public formatBasicForm(data: unknown): Partial<UpdateUserInput> {
    const form = data as Record<string, unknown>;
    const fieldVisibilities = formatFieldVisibilities(form);

    return {
      dateOfBirth: isNonNullable(form[EUserFormFields.dateOfBirth])
        ? (form[EUserFormFields.dateOfBirth] as string)
        : null,
      email: isNonNullable(form[EUserFormFields.email])
        ? (form[EUserFormFields.email] as string)
        : undefined,
      fieldVisibilities,
      firstname: isNonNullable(form[EUserFormFields.firstname])
        ? (form[EUserFormFields.firstname] as string)
        : undefined,
      lastname: isNonNullable(form[EUserFormFields.lastname])
        ? (form[EUserFormFields.lastname] as string)
        : undefined,
      phoneNumber: form[EUserFormFields.phone] as string | undefined,
      picture: (form[EUserFormFields.picture] as { url?: string } | undefined)?.url || null,
    };
  }
}
